/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useTranslation } from 'react-i18next';
import { Typography } from '@/components/typography';
import { cn } from '@/lib/utils';

interface UnavailablePriceProps {
    labelForA11y?: string;
    className?: string;
}

/**
 * Rendered in place of a price when the product has no price in the shopper's
 * active price books (legacy SFRA `Money.NOT_AVAILABLE`). Replaces the previous
 * behavior of coercing a missing price to `$0.00`. The Add-to-Cart button is
 * gated separately (see `useProductActions.canAddToCart`) so an unpriced item is
 * not purchasable, matching legacy parity.
 */
export default function UnavailablePrice({ labelForA11y, className }: UnavailablePriceProps) {
    const { t } = useTranslation('product');
    const label = t('price.unavailable');
    const a11yLabel = t('price.unavailableA11y');

    return (
        <>
            <Typography
                as="span"
                data-testid="price-unavailable"
                className={cn('text-muted-foreground font-semibold', className)}
                aria-label={a11yLabel}>
                {label}
            </Typography>
            <span className="sr-only" aria-live="polite" aria-atomic={true}>
                {labelForA11y} {a11yLabel}
            </span>
        </>
    );
}
